import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import UndoIcon from '@mui/icons-material/Undo';
import PrintIcon from '@mui/icons-material/Print';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  ConfirmationDialog,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common/components';
import {
  useConfirmConsumption,
  useConsumption,
  usePrintConsumptionDelivery,
} from '../../../hooks/useConsumptions';
import { useExternalOrder } from '../../../hooks/useExternalOrders';
import { useWarehouse } from '../../../hooks/useWarehouses';
import { CONSUMPTION_STATUSES } from '../constants/consumptionsConstants';
import {
  formatConsumptionDate,
  formatConsumptionItemStatus,
  formatConsumptionQuantity,
  formatConsumptionStatus,
  getConsumptionApiErrorMessage,
  isDeliveredConsumption,
  mapConsumptionToDisplay,
} from '../utils/consumptionsUtils';
import {
  getArticleLabel,
  useInventoryArticlesByUuids,
} from '../hooks/useConsumptionArticles';
import DeliverConsumptionDialog from './DeliverConsumptionDialog';
import ReverseConsumptionDialog from './ReverseConsumptionDialog';
import { usePermissions } from '../../../hooks/usePermissions';
import { useReportPendingState } from '../../hooks/useReportPendingState';
import { guardDialogClose, isAnyActionPending } from '../../utils/actionLockUtils';

const ConsumptionDetailDialog = ({
  open,
  onClose,
  consumptionId,
  onRefreshList,
  onPendingChange,
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [nestedActionPending, setNestedActionPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultMessage, setResultMessage] = useState('');

  const {
    data: consumption,
    isLoading,
    refetch,
  } = useConsumption(consumptionId, {
    enabled: open && Boolean(consumptionId),
  });

  const { data: externalOrder } = useExternalOrder(consumption?.uuid_external_order, {
    enabled: open && Boolean(consumption?.uuid_external_order),
  });

  const { data: warehouse } = useWarehouse(consumption?.uuid_warehouse, {
    enabled: open && Boolean(consumption?.uuid_warehouse),
  });

  const confirmMutation = useConfirmConsumption();
  const printMutation = usePrintConsumptionDelivery();
  const { permissions } = usePermissions();

  const articleUuids = useMemo(
    () => (consumption?.items || []).map((item) => item.uuid_inventory_article),
    [consumption?.items]
  );

  const { articles: inventoryArticles } = useInventoryArticlesByUuids(
    articleUuids,
    open && Boolean(consumption)
  );

  const articlesByUuid = useMemo(
    () =>
      inventoryArticles.reduce((acc, article) => {
        if (article?.uuid) {
          acc[article.uuid] = article;
        }
        return acc;
      }, {}),
    [inventoryArticles]
  );

  const displayConsumption = useMemo(() => {
    if (!consumption) return null;

    return mapConsumptionToDisplay(consumption, {
      ordersByUuid: externalOrder ? { [externalOrder.uuid]: externalOrder } : {},
      warehousesByUuid: warehouse ? { [warehouse.uuid]: warehouse } : {},
    });
  }, [consumption, externalOrder, warehouse]);

  useEffect(() => {
    if (open) {
      setErrorMessage('');
      setResultMessage('');
    }
  }, [open, consumptionId]);

  const canConfirm =
    permissions.canConfirmConsumptions &&
    displayConsumption?.status === CONSUMPTION_STATUSES.GENERATED;
  const canDeliver =
    permissions.canDeliverConsumptions &&
    displayConsumption?.status === CONSUMPTION_STATUSES.CONFIRMED;
  const canReverse =
    permissions.canManageConsumptions &&
    (displayConsumption?.status === CONSUMPTION_STATUSES.GENERATED ||
      displayConsumption?.status === CONSUMPTION_STATUSES.CONFIRMED);
  const canPrintDelivery =
    isDeliveredConsumption(consumption) || isDeliveredConsumption(displayConsumption);

  const isActionLocked = isAnyActionPending(
    confirmMutation.isPending,
    printMutation.isPending,
    nestedActionPending
  );

  useReportPendingState(isActionLocked, onPendingChange);

  const handleRefresh = async () => {
    await refetch();
    onRefreshList?.();
  };

  const handleConfirm = async () => {
    if (!consumption?.uuid) return;

    try {
      setErrorMessage('');
      await confirmMutation.mutateAsync({ id: consumption.uuid, payload: {} });
      setConfirmDialogOpen(false);
      await handleRefresh();
    } catch (error) {
      setErrorMessage(
        getConsumptionApiErrorMessage(error, 'No se pudo confirmar el consumo')
      );
      setConfirmDialogOpen(false);
    }
  };

  const handleActionSuccess = async (_consumption, meta = {}) => {
    await handleRefresh();

    if (meta.pdfGenerated === false) {
      const printErrorMessage = meta.printError
        ? getConsumptionApiErrorMessage(
            meta.printError,
            'No se pudo generar el comprobante PDF para firmas'
          )
        : 'No se pudo generar el comprobante PDF para firmas';
      setErrorMessage(`Entrega registrada. ${printErrorMessage}`);
      return;
    }

    if (meta.pdfGenerated) {
      setResultMessage('Entrega registrada. Se descargó el comprobante PDF para firmas.');
    }
  };

  const handlePrintDelivery = async () => {
    if (!consumption?.uuid || isActionLocked) return;

    try {
      setErrorMessage('');
      await printMutation.mutateAsync(consumption.uuid);
      setResultMessage('Comprobante PDF generado correctamente.');
    } catch (error) {
      setErrorMessage(
        getConsumptionApiErrorMessage(error, 'No se pudo generar el comprobante PDF')
      );
    }
  };

  if (!open) return null;

  if (isLoading || !displayConsumption) {
    return (
      <DialogContainer open={open} onClose={onClose} size="large" minHeight="60vh">
        <DialogTitle>Detalle del consumo</DialogTitle>
        <DialogBody>
          <TableBodySkeleton />
        </DialogBody>
        <DialogActions
          handleClose={onClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>
    );
  }

  return (
    <>
      <DialogContainer
        open={open}
        onClose={guardDialogClose(isActionLocked, onClose)}
        size="large"
        minHeight="70vh"
      >
        <DialogTitle>Consumo {displayConsumption.consumption_number}</DialogTitle>

        <DialogBody>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {resultMessage && <Alert severity="info">{resultMessage}</Alert>}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label={`Estado: ${formatConsumptionStatus(displayConsumption.status)}`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`Orden técnica: ${displayConsumption.order_number}`}
                variant="outlined"
              />
              <Chip
                label={`Depósito: ${displayConsumption.warehouse_label}`}
                variant="outlined"
              />
              <Chip
                label={`Fecha: ${displayConsumption.created_at_label}`}
                variant="outlined"
              />
            </Box>

            {displayConsumption.observations && (
              <Typography variant="body2" color="text.secondary">
                Observaciones: {displayConsumption.observations}
              </Typography>
            )}

            <Typography variant="subtitle1" fontWeight={700}>
              Artículos consumidos
            </Typography>

            <GeneralTableBox>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <HeadTableCell>Artículo</HeadTableCell>
                    <HeadTableCell>Cantidad</HeadTableCell>
                    <HeadTableCell>Reserva consumida</HeadTableCell>
                    <HeadTableCell>Estado</HeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(displayConsumption.items || []).length === 0 ? (
                    <TableRow>
                      <RowTableCell colSpan={4} sx={{ textAlign: 'center', py: 3 }}>
                        Sin artículos cargados
                      </RowTableCell>
                    </TableRow>
                  ) : (
                    (displayConsumption.items || []).map((item) => (
                      <TableRow key={item.uuid}>
                        <RowTableCell>
                          {getArticleLabel(articlesByUuid, item.uuid_inventory_article)}
                        </RowTableCell>
                        <RowTableCell>{formatConsumptionQuantity(item.quantity)}</RowTableCell>
                        <RowTableCell>
                          {formatConsumptionQuantity(item.reserved_quantity_consumed)}
                        </RowTableCell>
                        <RowTableCell>
                          {formatConsumptionItemStatus(item.status)}
                        </RowTableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </GeneralTableBox>

            <Typography variant="subtitle1" fontWeight={700}>
              Datos de entrega
            </Typography>

            <Box sx={{ display: 'grid', gap: 1 }}>
              <Typography variant="body2">
                Responsable: {displayConsumption.delivery_responsible || '-'}
              </Typography>
              <Typography variant="body2">
                Dirección: {displayConsumption.delivery_address || '-'}
              </Typography>
              <Typography variant="body2">
                Fecha de entrega: {formatConsumptionDate(displayConsumption.delivered_at)}
              </Typography>
              <Typography variant="body2">
                Observaciones de entrega: {displayConsumption.delivery_observations || '-'}
              </Typography>
              {displayConsumption.reversal_reason && (
                <Typography variant="body2" color="warning.main">
                  Motivo de reversión: {displayConsumption.reversal_reason}
                </Typography>
              )}
            </Box>

            {(canConfirm || canDeliver || canReverse || canPrintDelivery) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {canPrintDelivery && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintDelivery}
                    disabled={isActionLocked || printMutation.isPending}
                  >
                    Imprimir comprobante
                  </Button>
                )}
                {canConfirm && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => setConfirmDialogOpen(true)}
                    disabled={isActionLocked}
                  >
                    Confirmar
                  </Button>
                )}
                {canDeliver && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<LocalShippingIcon />}
                    onClick={() => setDeliverDialogOpen(true)}
                    disabled={isActionLocked}
                  >
                    Entregar
                  </Button>
                )}
                {canReverse && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<UndoIcon />}
                    onClick={() => setReverseDialogOpen(true)}
                    disabled={isActionLocked}
                  >
                    Revertir
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </DialogBody>

        <DialogActions
          handleClose={onClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
        cancelButtonProps={{ disabled: isActionLocked }}
        cancelLabel="Cerrar"
      />
      </DialogContainer>

      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => !isActionLocked && setConfirmDialogOpen(false)}
        onConfirm={handleConfirm}
        title="Confirmar consumo"
        message={`¿Confirmás el consumo ${displayConsumption.consumption_number}?`}
        confirmText="Confirmar"
        cancelText="Cancelar"
        confirmVariant="base"
        isLoading={confirmMutation.isPending}
      />

      <DeliverConsumptionDialog
        open={deliverDialogOpen}
        onClose={() => setDeliverDialogOpen(false)}
        consumption={displayConsumption}
        onSuccess={handleActionSuccess}
        onPendingChange={setNestedActionPending}
      />

      <ReverseConsumptionDialog
        open={reverseDialogOpen}
        onClose={() => setReverseDialogOpen(false)}
        consumption={displayConsumption}
        onSuccess={handleActionSuccess}
        onPendingChange={setNestedActionPending}
      />
    </>
  );
};

export default ConsumptionDetailDialog;
